import { useGetMessageTemplates, useGetProgramById } from '@frontend/app/hooks';
import { GetOffersBySearchQuery_offers } from '@frontend/applications/AffiliatesApp/queries/types/GetOffersBySearchQuery';
import { OFFER_TYPE, SEND_STA_TASK_ID } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { useAppContext } from '@frontend/context/AppContext';
import { useMemo } from 'react';

interface IProps {
  offers: GetOffersBySearchQuery_offers[];
  taskId: SEND_STA_TASK_ID;
}

export const useGetDefaultEmailTemplate = (props: IProps) => {
  const { offers, taskId } = props;

  const programId = offers?.[0]?.programId;

  const offerId = useMemo(() => {
    if (!offers?.length) return null;
    switch (taskId) {
      case SEND_STA_TASK_ID.send_promo_task:
        return offers.find((offer) => offer.type === OFFER_TYPE.PROMO_CODE)?.id;
      case SEND_STA_TASK_ID.send_link_task:
        return offers.find((offer) => offer.type === OFFER_TYPE.LINK)?.id;
      default:
        return null;
    }
  }, [offers, taskId]);

  const { apolloClient } = useAppContext();

  const { templates, loading: messageTemplatesLoading } = useGetMessageTemplates({
    variables: {
      programId,
    },
    client: apolloClient,
    skip: !programId,
  });

  const { data: programData, loading: programLoading } = useGetProgramById({
    variables: {
      id: programId,
    },
    client: apolloClient,
    skip: !programId,
  });

  const defaultOffersMap = programData?.program?.programMetadata?.defaultOffersEmailTemplate;

  const defaultTemplateIdForOffer = useMemo(() => {
    if (!offerId || !defaultOffersMap) return undefined;
    return defaultOffersMap[offerId];
  }, [offerId, defaultOffersMap]);

  return useMemo(() => {
    if (messageTemplatesLoading || programLoading || !defaultTemplateIdForOffer || !templates?.length) return null;

    const foundTemplate = templates.find(
      (template) => template.id === defaultTemplateIdForOffer
    );
    return foundTemplate || null;
  }, [templates, messageTemplatesLoading, programLoading, defaultTemplateIdForOffer]);
};
